(() => {
	const MAX_TEST_RACKETS_PER_ORDER = 2;
	const SCROLL_END_THRESHOLD = 4;
	// Companion variant kept in sync with the cart's test-racket lines: present
	// (quantity 1) as soon as the cart holds at least one test racket, whatever
	// its quantity or which racket it is, and removed again once none remain.
	const COMPANION_VARIANT_ID = 57489654219132;

	let testRacketQuantity = 0;
	const gates = [];

	const addUrl = () => (window.theme && theme.routes && theme.routes.cart_add_url) || "/cart/add.js";
	const changeUrl = () => (window.theme && theme.routes && theme.routes.cart_change_url) || "/cart/change.js";

	const countTestRackets = (cart) =>
		cart.items.reduce((total, item) => {
			if (item.properties && item.properties._test_racket === "true") {
				return total + item.quantity;
			}
			return total;
		}, 0);

	// Matches the pattern used everywhere else in this theme (ProductForm,
	// CartItems, CordageModal, gift-wrapping...) to let the currently mounted
	// cart-drawer/main-cart register their own section id, so the request
	// below gets back rendered HTML for them instead of only raw cart JSON.
	const getBundledSections = () => {
		const sections = [];
		document.documentElement.dispatchEvent(
			new CustomEvent("cart:bundled-sections", { bubbles: true, detail: { sections } }),
		);
		return sections;
	};

	const publishCartUpdate = (source, cart) => {
		if (window.theme && theme.pubsub) {
			theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source, cart });
		}
	};

	// Called on every cart change (any product, any page): keeps the companion
	// line a pure reflection of "is there a test racket in the cart or not",
	// instead of something only the test-racket popup knows how to add. This
	// also makes it self-healing — removing the last test racket from the cart
	// page, the drawer, or elsewhere, removes the companion the same way.
	const syncCompanionItem = (cart) => {
		const hasTestRacket = countTestRackets(cart) > 0;
		const companionLine = cart.items.find((item) => item.variant_id === COMPANION_VARIANT_ID);

		if (hasTestRacket && !companionLine) {
			fetch(addUrl(), {
				method: "POST",
				// Matches theme.utils.fetchConfig("javascript"), the header combo the
				// theme's own ProductForm/CordageModal use against this same endpoint.
				headers: { "Content-Type": "application/json", Accept: "application/javascript" },
				body: JSON.stringify({
					items: [{ id: COMPANION_VARIANT_ID, quantity: 1 }],
					sections: getBundledSections(),
					sections_url: window.location.pathname,
				}),
			})
				.then((response) => response.json())
				.then(async (parsedState) => {
					if (parsedState.status) {
						console.error("[racket-test] companion add failed:", parsedState);
						return;
					}

					// /cart/add.js, posted with an `items` array, only returns the line(s)
					// that were just added — not the full cart. Using that partial object
					// as-is would make the next sync pass think the test racket itself is
					// gone (it isn't in this response), undoing the add it just made and
					// looping forever. Fetch the real, full cart before publishing.
					const fullCart = await (await fetch("/cart.js")).json();
					fullCart.sections = parsedState.sections;
					publishCartUpdate("racket-test-companion", fullCart);
				})
				.catch((error) => console.error("[racket-test] companion add errored:", error));
			return;
		}

		if (!hasTestRacket && companionLine) {
			fetch(changeUrl(), {
				method: "POST",
				headers: { "Content-Type": "application/json", Accept: "application/javascript" },
				body: JSON.stringify({
					id: companionLine.key,
					quantity: 0,
					sections: getBundledSections(),
					sections_url: window.location.pathname,
				}),
			})
				.then((response) => response.json())
				.then((parsedState) => {
					if (parsedState.status) {
						console.error("[racket-test] companion removal failed:", parsedState);
						return;
					}
					publishCartUpdate("racket-test-companion", parsedState);
				})
				.catch((error) => console.error("[racket-test] companion removal errored:", error));
		}
	};

	const isScrolledToEnd = (scrollable) =>
		scrollable.scrollHeight - scrollable.scrollTop - scrollable.clientHeight <=
		SCROLL_END_THRESHOLD;

	const isLimitReached = () => testRacketQuantity >= MAX_TEST_RACKETS_PER_ORDER;

	const updateGate = (gate) => {
		const limitReached = isLimitReached();
		gate.confirmButton.disabled =
			limitReached || !isScrolledToEnd(gate.scrollable);
		gate.limitError.hidden = !limitReached;
	};

	const updateAllGates = () => gates.forEach(updateGate);

	// Registered on `document` in the capture phase, so this runs before theme.js's
	// own "product-form" custom element gets a chance to react — it does NOT listen
	// for the form's `submit` event at all; it binds a `click` listener directly to
	// the `[type="submit"]` button itself (ProductForm.connectedCallback) and calls
	// `event.preventDefault()` on that click, so the form's `submit` event never
	// actually fires. Intercepting `click` in the capture phase on `document` (an
	// ancestor) is what lets us run first and veto that handler with
	// stopImmediatePropagation — a `submit` listener here would never see anything.
	document.addEventListener(
		"click",
		(event) => {
			// The cordage module's own "Tester avec le cordage de mon choix" button
			// (data-racket-test-modal, in main-product.liquid) keeps data-open-modal
			//="cordage" — module-cordage.js reads data-variant-id/cost/pose-variants
			// off that exact attribute once, at script load, to build its module-level
			// pose-variant lookup table, so removing it there breaks stringing-fee
			// resolution entirely. Instead, veto its own click->open() here (same
			// button, same event, but CordageModal's listener is registered later and
			// in the bubble phase, so our capture-phase stopImmediatePropagation wins)
			// and open our conditions popup first; confirming it there re-opens the
			// cordage module afterwards (handled by the pendingAction branch below).
			const cordageTrigger = event.target.closest("[data-racket-test-modal]");
			if (cordageTrigger) {
				const modal = document.getElementById(
					cordageTrigger.getAttribute("data-racket-test-modal"),
				);
				const gate = gates.find((g) => g.scrollable.closest("x-modal") === modal);
				if (modal && gate) {
					event.preventDefault();
					event.stopImmediatePropagation();
					gate.pendingAction = "open-cordage";
					modal.show(cordageTrigger);
				}
				return;
			}

			const button = event.target.closest("[data-racket-test-confirm]");
			if (!button) return;

			const gate = gates.find((g) => g.confirmButton === button);
			if (!gate) return;

			// Confirming the popup opened via the cordage trigger above should open
			// the cordage module instead of adding the racket to the cart directly —
			// the cart add happens later, from inside that module, once the stringing
			// has been configured.
			if (gate.pendingAction === "open-cordage") {
				event.preventDefault();
				event.stopImmediatePropagation();
				gate.pendingAction = null;
				gate.scrollable.closest("x-modal")?.hide();
				document.querySelector("cordage-modal")?.open();
				return;
			}

			// Safety net for the (disabled-by-default-when-at-limit) confirm button —
			// e.g. if the cart changed in another tab since the last refresh.
			if (isLimitReached()) {
				event.preventDefault();
				event.stopImmediatePropagation();
				gate.limitError.hidden = false;
			}
		},
		true,
	);

	const closeOpenModal = () => {
		document
			.querySelectorAll('x-modal[id^="RacketTestModal-"][open]')
			.forEach((modal) => {
				modal.hide();
			});
	};

	if (window.theme && window.theme.pubsub) {
		theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
			const cart = event && event.cart;
			if (!cart || !cart.items) return;

			testRacketQuantity = countTestRackets(cart);
			updateAllGates();
			closeOpenModal();
			syncCompanionItem(cart);
		});
	}

	const initGate = (scrollable) => {
		const modal = scrollable.closest("x-modal");
		const confirmButton = modal?.querySelector("[data-racket-test-confirm]");
		const limitError = modal?.querySelector('[id^="RacketTestLimitError-"]');
		if (!confirmButton || !limitError) return;

		// Liquid already renders the button/error in the correct initial state from
		// `cart.items` at page-render time (see data-racket-test-cart-quantity), so
		// there's no gap where an unset JS-side count could let a stale "enabled"
		// state slip through before the /cart.js fetch below resolves.
		const initialQuantity = parseInt(
			confirmButton.getAttribute("data-racket-test-cart-quantity"),
			10,
		);
		if (!Number.isNaN(initialQuantity)) {
			testRacketQuantity = Math.max(testRacketQuantity, initialQuantity);
		}

		const gate = { scrollable, confirmButton, limitError, pendingAction: null };
		gates.push(gate);

		scrollable.addEventListener("scroll", () => updateGate(gate), {
			passive: true,
		});

		// Re-checks whenever the (initially hidden, so zero-size) popup gets its
		// real dimensions once opened, in case its content already fits without
		// needing to scroll at all.
		new ResizeObserver(() => updateGate(gate)).observe(scrollable);

		// Clears any pending action (see the cordage-trigger handling above) on
		// every *other* button that can open this modal — namely the main
		// "Commander ma raquette test" button — so a previous cordage visit that
		// was cancelled can't leak into a later direct add-to-cart.
		document
			.querySelectorAll(`[aria-controls="${modal.id}"]`)
			.forEach((button) => {
				button.addEventListener("click", () => {
					gate.pendingAction = button.getAttribute("data-racket-test-pending-action");
				});
			});
	};

	document.querySelectorAll("[data-racket-test-scrollable]").forEach(initGate);

	// Initial load: no cartUpdate event has fired yet, so check/sync once
	// against the cart as it stands (e.g. a companion manually removed in a
	// previous session, or the page was simply reloaded).
	fetch("/cart.js")
		.then((response) => response.json())
		.then((cart) => {
			testRacketQuantity = countTestRackets(cart);
			updateAllGates();
			syncCompanionItem(cart);
		})
		.catch(() => {});
})();
